export type SseEvent = {
  event: string;
  data: string;
};

const DEFAULT_EVENT = "message";

const readFieldValue = (line: string, fieldLength: number): string => {
  const value = line.slice(fieldLength + 1);
  return value.startsWith(" ") ? value.slice(1) : value;
};

class SseParser {
  private buffer = "";
  private eventName = "";
  private dataLines: string[] = [];

  push(text: string): SseEvent[] {
    this.buffer += text;
    const events: SseEvent[] = [];

    for (const line of this.takeCompleteLines()) {
      const event = this.consumeLine(line);
      if (event) events.push(event);
    }

    return events;
  }

  flush(): SseEvent[] {
    const events: SseEvent[] = [];

    if (this.buffer.length > 0) {
      const event = this.consumeLine(this.buffer);
      this.buffer = "";
      if (event) events.push(event);
    }

    const pending = this.dispatch();
    if (pending) events.push(pending);

    return events;
  }

  private takeCompleteLines(): string[] {
    const lines: string[] = [];
    let start = 0;

    for (let i = 0; i < this.buffer.length; i++) {
      const ch = this.buffer[i];
      if (ch !== "\n" && ch !== "\r") continue;

      if (ch === "\r" && i === this.buffer.length - 1) break;

      lines.push(this.buffer.slice(start, i));
      if (ch === "\r" && this.buffer[i + 1] === "\n") i++;
      start = i + 1;
    }

    this.buffer = this.buffer.slice(start);
    return lines;
  }

  private consumeLine(line: string): SseEvent | null {
    if (line === "") return this.dispatch();
    if (line.startsWith(":")) return null;

    if (line === "data" || line.startsWith("data:")) {
      this.dataLines.push(line === "data" ? "" : readFieldValue(line, 4));
    } else if (line === "event" || line.startsWith("event:")) {
      this.eventName = line === "event" ? "" : readFieldValue(line, 5).trim();
    }

    return null;
  }

  private dispatch(): SseEvent | null {
    const hasContent = this.dataLines.length > 0 || this.eventName !== "";
    const event = hasContent
      ? { event: this.eventName || DEFAULT_EVENT, data: this.dataLines.join("\n") }
      : null;

    this.eventName = "";
    this.dataLines = [];
    return event;
  }
}

export async function* readSseEvents(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal
): AsyncGenerator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();

  try {
    while (true) {
      signal?.throwIfAborted();

      const { done, value } = await reader.read();
      if (done) break;

      yield* parser.push(decoder.decode(value, { stream: true }));
    }

    yield* parser.push(decoder.decode());
    yield* parser.flush();
  } finally {
    try {
      await reader.cancel();
    } catch {
      // the stream is already closed or errored
    }
  }
}
